import { useEffect } from "react";

interface PageSEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogImage?: string;
  schema?: object;
}

export default function PageSEO({
  title,
  description,
  keywords,
  canonical,
  ogImage = "https://readdy.ai/api/search-image?query=elegant%20taiwanese%20women%20fashion%20boutique%20outlet%20designer%20brand%20quality%20graceful%20style&width=1200&height=630&seq=og_default&orientation=landscape",
  schema,
}: PageSEOProps) {
  useEffect(() => {
    // Title
    document.title = title;

    // Helper
    const setMeta = (selector: string, attr: string, content: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr.split("=")[0], attr.split("=")[1]);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement("link");
        el.rel = rel;
        document.head.appendChild(el);
      }
      el.href = href;
    };

    // Meta tags
    setMeta('meta[name="description"]', "name=description", description);
    setMeta('meta[name="keywords"]', "name=keywords", keywords);
    setMeta('meta[name="robots"]', "name=robots", "index,follow");
    setMeta('meta[name="geo.region"]', "name=geo.region", "TW-TXG");
    setMeta('meta[name="geo.placename"]', "name=geo.placename", "台中市");
    setMeta('meta[name="geo.position"]', "name=geo.position", "24.1477;120.6736");
    setMeta('meta[name="ICBM"]', "name=ICBM", "24.1477, 120.6736");

    // Open Graph
    setMeta('meta[property="og:title"]', "property=og:title", title);
    setMeta('meta[property="og:description"]', "property=og:description", description);
    setMeta('meta[property="og:type"]', "property=og:type", "website");
    setMeta('meta[property="og:image"]', "property=og:image", ogImage);
    setMeta('meta[property="og:locale"]', "property=og:locale", "zh_TW");
    setMeta('meta[property="og:site_name"]', "property=og:site_name", "台中設計師女裝Outlet");

    // Twitter Card
    setMeta('meta[name="twitter:card"]', "name=twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name=twitter:title", title);
    setMeta('meta[name="twitter:description"]', "name=twitter:description", description);
    setMeta('meta[name="twitter:image"]', "name=twitter:image", ogImage);

    // Canonical
    const canonicalUrl = canonical || window.location.href;
    setLink("canonical", canonicalUrl);

    // Last modified
    setMeta('meta[name="last-modified"]', "name=last-modified", new Date().toISOString().split("T")[0]);

    // Schema.org JSON-LD
    if (schema) {
      const existing = document.getElementById("page-schema-ld");
      if (existing) existing.remove();
      const script = document.createElement("script");
      script.id = "page-schema-ld";
      script.type = "application/ld+json";
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    return () => {
      const schemaEl = document.getElementById("page-schema-ld");
      if (schemaEl) schemaEl.remove();
    };
  }, [title, description, keywords, canonical, ogImage, schema]);

  return null;
}
